import { useId, type ComponentPropsWithRef, type ReactNode } from "react";
import { cn } from "./cn";
import { controlClasses, describedBy, FieldShell, type ControlSize } from "./Field";

export interface InputProps extends Omit<ComponentPropsWithRef<"input">, "size"> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  size?: ControlSize;
  icon?: ReactNode;
  iconRight?: ReactNode;
  containerClassName?: string;
}

export function Input({
  label,
  hint,
  error,
  size = "md",
  icon,
  iconRight,
  containerClassName,
  className,
  id,
  required,
  ...rest
}: InputProps) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  const hasIcon = icon !== undefined && icon !== null;
  const hasIconRight = iconRight !== undefined && iconRight !== null;

  const control = (
    <input
      id={fieldId}
      required={required}
      aria-invalid={error ? true : undefined}
      aria-describedby={describedBy(fieldId, !!error, !!hint)}
      className={cn(
        controlClasses(!!error, size),
        hasIcon ? "pl-9" : undefined,
        hasIconRight ? "pr-9" : undefined,
        className,
      )}
      {...rest}
    />
  );

  return (
    <FieldShell
      id={fieldId}
      label={label}
      hint={hint}
      error={error}
      required={required}
      className={containerClassName}
    >
      {hasIcon || hasIconRight ? (
        <div className="relative">
          {hasIcon ? (
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-content-muted">
              {icon}
            </span>
          ) : null}
          {control}
          {hasIconRight ? (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-content-muted">
              {iconRight}
            </span>
          ) : null}
        </div>
      ) : (
        control
      )}
    </FieldShell>
  );
}
